'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * ScrollAnimation Component (IntersectionObserver-based)
 * Adds smooth scroll-triggered animations to children using native browser observer and GPU-accelerated CSS transitions.
 */
export default function ScrollAnimation({
  children,
  animation = 'fade-in-up', // 'fade-in-up', 'fade-in', 'slide-in-left', 'slide-in-right'
  delay = 0,
  className = '',
  once = true,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            observer.unobserve(entry.target);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        rootMargin: '-80px',
      }
    );

    const el = ref.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [once]);

  let animationClass = '';
  if (animation === 'fade-in-up') {
    animationClass = isVisible 
      ? 'opacity-100 translate-y-0' 
      : 'opacity-0 translate-y-[35px]';
  } else if (animation === 'slide-in-left') {
    animationClass = isVisible 
      ? 'opacity-100 translate-x-0' 
      : 'opacity-0 -translate-x-[35px]';
  } else if (animation === 'slide-in-right') {
    animationClass = isVisible 
      ? 'opacity-100 translate-x-0' 
      : 'opacity-0 translate-x-[35px]';
  } else {
    // default fade-in
    animationClass = isVisible ? 'opacity-100' : 'opacity-0';
  }

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-[cubic-bezier(0.21,1.02,0.43,1.01)] ${animationClass} ${className}`}
      style={{
        transitionDelay: `${delay}s`,
        willChange: 'transform, opacity',
      }}
    >
      {children}
    </div>
  );
}
